<template>
  <div class="tw-flex tw-flex-col tw-gap-2 tw-text-sm">
    <div class="tw-flex tw-items-center tw-justify-between">
      <div class="tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wide">
        SolarEdge
        <span class="tw-font-normal tw-normal-case tw-text-gray-400">
          opgeslagen<span v-if="status?.site_id"> · site {{ status.site_id }}</span>
        </span>
      </div>
      <div class="tw-flex tw-items-center tw-gap-3">
        <a
          v-if="status?.site_id"
          :href="`https://monitoring.solaredge.com/solaredge-web/p/site/${status.site_id}`"
          target="_blank"
          rel="noopener"
          class="tw-text-xs tw-text-blue-600 hover:tw-underline"
        >Openen in SolarEdge</a>
      </div>
    </div>

    <div v-if="isFetching && !status" class="tw-flex tw-items-center tw-py-2 tw-text-gray-400">
      <span class="tw-w-4 tw-h-4 tw-border-2 tw-border-gray-300 tw-border-t-blue-400 tw-rounded-full tw-animate-spin tw-mr-2" />
      Opgeslagen SolarEdge-gegevens laden...
    </div>
    <div v-else-if="error" class="tw-text-red-600 tw-bg-red-50 tw-rounded tw-p-2">
      {{ error.message || error.detail || 'SolarEdge-status ophalen mislukt' }}
    </div>

    <template v-else-if="status">
      <div v-for="(msg, i) in [...status.warnings, ...liveWarnings]" :key="i" class="tw-text-xs tw-text-amber-700 tw-bg-amber-50 tw-rounded tw-p-2">
        {{ msg }}
      </div>

      <!-- Site -->
      <div class="tw-bg-gray-50 tw-rounded-lg tw-p-3">
        <div class="tw-flex tw-items-center tw-justify-between tw-mb-1">
          <div class="tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wide">Site</div>
          <live-read-button
            :credits="PART_CREDITS.site"
            :read-at="liveAt.site ?? null"
            :reading="readingPart === 'site'"
            :disabled="readingPart !== null"
            @read="readLive('site')"
          />
        </div>
        <div class="tw-grid tw-grid-cols-2 tw-gap-x-4 tw-gap-y-1">
          <span class="tw-text-gray-500">Naam</span>
          <span class="tw-font-medium">{{ site?.name || '—' }}</span>
          <template v-if="siteAddress">
            <span class="tw-text-gray-500">Adres bij SolarEdge</span>
            <span class="tw-font-medium">{{ siteAddress }}</span>
          </template>
          <span class="tw-text-gray-500">Status</span>
          <span class="tw-font-medium" :class="siteStatus === 'ACTIVE' ? 'tw-text-green-600' : 'tw-text-red-600'">
            {{ siteStatus || 'Onbekend' }}
          </span>
          <span class="tw-text-gray-500">Piekvermogen</span>
          <span class="tw-font-medium">{{ peakPower }}</span>
          <span class="tw-text-gray-500">Installatie</span>
          <span class="tw-font-medium">{{ site?.installation_date ? formatDateNL(site.installation_date) : '—' }}</span>
          <template v-if="live.site?.last_update_time">
            <span class="tw-text-gray-500">Laatste update</span>
            <span class="tw-font-medium" :class="ageClass(live.site.last_update_time)">
              {{ formatDateTimeNL(live.site.last_update_time) }}
            </span>
          </template>
          <span class="tw-text-gray-500">Uitgelezen via</span>
          <span class="tw-font-medium">{{ status.switched ? 'SolarEdge' : 'Cast4all (nog niet omgezet)' }}</span>
        </div>
      </div>

      <!-- Omvormers -->
      <div class="tw-bg-gray-50 tw-rounded-lg tw-p-3">
        <div class="tw-flex tw-items-center tw-justify-between tw-mb-1">
          <div class="tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wide">
            Omvormers <span class="tw-font-normal tw-normal-case">({{ activeCount }} actief)</span>
          </div>
          <live-read-button
            :credits="PART_CREDITS.inverters"
            :read-at="liveAt.inverters ?? null"
            :reading="readingPart === 'inverters'"
            :disabled="readingPart !== null"
            @read="readLive('inverters')"
          />
        </div>
        <table v-if="inverters.length" class="tw-w-full tw-text-xs">
          <thead>
            <tr class="tw-text-gray-500 tw-text-left">
              <th class="tw-py-1 tw-pr-2">Serienummer</th>
              <th class="tw-py-1 tw-pr-2">Type</th>
              <th class="tw-py-1 tw-pr-2">Firmware</th>
              <th class="tw-py-1 tw-pr-2">Comm.</th>
              <th class="tw-py-1 tw-pr-2">Optim.</th>
              <th class="tw-py-1 tw-pr-2" title="Totale opwek volgens de omvormerteller">Totaal</th>
              <th v-if="live.inverters" class="tw-py-1 tw-pr-2">Nu</th>
              <th class="tw-py-1 tw-pr-2" title="Nieuwste tellerstand die de Norm-ingest heeft opgehaald">Laatste meting</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="inv in inverters"
              :key="inv.serial_number"
              class="tw-border-t tw-border-gray-200"
              :class="inv.active === false ? 'tw-text-gray-400' : ''"
            >
              <td class="tw-py-1 tw-pr-2 tw-font-mono" :title="inv.se_created_at ? `Bij SolarEdge aangemeld ${formatDateNL(inv.se_created_at)}` : ''">
                {{ inv.serial_number }}
                <span v-if="inv.active === false" class="tw-font-sans">(vervangen)</span>
              </td>
              <td class="tw-py-1 tw-pr-2">
                {{ inv.part_number || '—' }}
                <span v-if="inv.nameplate_w" class="tw-text-gray-400">{{ (inv.nameplate_w / 1000).toLocaleString('nl-NL') }} kW</span>
              </td>
              <td class="tw-py-1 tw-pr-2 tw-font-mono">{{ inv.firmware || '—' }}</td>
              <td class="tw-py-1 tw-pr-2">{{ inv.communication_type || '—' }}</td>
              <td class="tw-py-1 tw-pr-2">{{ inv.connected_optimizers ?? '—' }}</td>
              <td class="tw-py-1 tw-pr-2">{{ inv.lifetime_wh != null ? `${Math.round(inv.lifetime_wh / 1000).toLocaleString('nl-NL')} kWh` : '—' }}</td>
              <td v-if="live.inverters" class="tw-py-1 tw-pr-2" :title="inv.telemetry_at ? `Kwartier van ${formatDateTimeNL(inv.telemetry_at)}` : ''">
                <template v-if="inv.power_w != null">
                  {{ Math.round(inv.power_w) }} W<span v-if="inv.ac_voltage != null" class="tw-text-gray-400"> · {{ Math.round(inv.ac_voltage) }} V</span>
                </template>
                <template v-else>—</template>
              </td>
              <td class="tw-py-1 tw-pr-2" :class="ageClass(inv.last_sample_at)">
                {{ inv.last_sample_at ? formatDateTimeNL(inv.last_sample_at) : '—' }}
              </td>
            </tr>
          </tbody>
        </table>
        <div v-else class="tw-text-xs tw-text-gray-400">Geen omvormers bekend</div>
      </div>

      <!-- Meldingen -->
      <div class="tw-bg-gray-50 tw-rounded-lg tw-p-3">
        <div class="tw-flex tw-items-center tw-justify-between tw-mb-1">
          <div class="tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wide">
            Open meldingen <span class="tw-font-normal tw-normal-case">({{ alerts.length }})</span>
          </div>
          <live-read-button
            :credits="PART_CREDITS.alerts"
            :read-at="liveAt.alerts ?? null"
            :reading="readingPart === 'alerts'"
            :disabled="readingPart !== null"
            @read="readLive('alerts')"
          />
        </div>
        <table v-if="alerts.length" class="tw-w-full tw-text-xs">
          <thead>
            <tr class="tw-text-gray-500 tw-text-left">
              <th class="tw-py-1 tw-pr-2">Melding</th>
              <th class="tw-py-1 tw-pr-2" title="Impact 1-9 volgens SolarEdge">Impact</th>
              <th class="tw-py-1 tw-pr-2">Onderdeel</th>
              <th class="tw-py-1 tw-pr-2">Sinds</th>
              <th class="tw-py-1 tw-pr-2">Laatst</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="a in alerts" :key="a.alert_id" class="tw-border-t tw-border-gray-200">
              <td class="tw-py-1 tw-pr-2">
                {{ alertLabel(a.type) }}
                <span v-if="a.error_codes?.length" class="tw-font-mono tw-text-gray-400">{{ a.error_codes.join(', ') }}</span>
              </td>
              <td class="tw-py-1 tw-pr-2" :class="a.impact >= 5 ? 'tw-text-red-600 tw-font-medium' : ''">{{ a.impact ?? '—' }}</td>
              <td class="tw-py-1 tw-pr-2">
                {{ a.component_name || '—' }}
                <span v-if="a.component_serial" class="tw-font-mono tw-text-gray-400">{{ a.component_serial }}</span>
              </td>
              <td class="tw-py-1 tw-pr-2">{{ a.first_trigger ? formatDateTimeNL(a.first_trigger) : '—' }}</td>
              <td class="tw-py-1 tw-pr-2">{{ a.last_trigger ? formatDateTimeNL(a.last_trigger) : '—' }}</td>
            </tr>
          </tbody>
        </table>
        <div v-else class="tw-text-xs tw-text-gray-400">Geen open meldingen</div>
      </div>

      <div class="tw-text-xs tw-text-gray-400">
        Opgeslagen gegevens uit energize (nachtelijke synchronisatie, site gezien {{ status.stored?.site?.last_seen_at ? formatDateTimeNL(status.stored.site.last_seen_at) : 'onbekend' }}).
        <span v-if="creditsRemaining != null">Nog {{ creditsRemaining.toLocaleString('nl-NL') }} SolarEdge-credits deze maand.</span>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import fetchData from '@/util/fetch'
import { formatDateNL, formatDateTimeNL } from './formatters'
import LiveReadButton from './LiveReadButton.vue'
import { useSolarEdgeStatus, formatPeakPower } from './composables/useSolarEdgeStatus'

const props = defineProps({
  serialNumber: { type: String, required: true },
})

// What energize stores is free and loads with the page. A live read costs SolarEdge credits, so it is
// per section and only on request: PART_CREDITS as in eleena-home solaredge.PART_CALLS.
const PART_CREDITS = { site: 1, inverters: 2, alerts: 1 }
const PART_KEYS = { site: 'site', inverters: 'inverters', alerts: 'open_alerts' }

const { data: status, isFetching, error } = useSolarEdgeStatus(computed(() => props.serialNumber))

// the live parts read so far: { site, inverters, open_alerts }, each with when it was read
const live = ref({})
const liveAt = ref({})
const liveWarnings = ref([])
const readingPart = ref(null)
const creditsRemaining = ref(null)
watch(() => props.serialNumber, () => {
  live.value = {}
  liveAt.value = {}
  liveWarnings.value = []
})

const readLive = async (part) => {
  readingPart.value = part
  try {
    const result = await fetchData(`/eleena/api/v1/meters/${props.serialNumber}/solaredge?live=${part}`)
    const data = result?.data
    const key = PART_KEYS[part]
    live.value = { ...live.value, [key]: data?.live?.[key] ?? null }
    liveAt.value = { ...liveAt.value, [part]: new Date() }
    liveWarnings.value = (data?.warnings ?? []).filter((w) => w.startsWith('SolarEdge '))
    creditsRemaining.value = data?.credits_remaining_month ?? creditsRemaining.value
  } catch (e) {
    liveWarnings.value = [e?.message || e?.detail || 'SolarEdge live uitlezen mislukt']
  } finally {
    readingPart.value = null
  }
}

// live where available, else what the nightly ingest stored in energize
const site = computed(() => live.value.site ?? status.value?.stored?.site ?? null)
const siteStatus = computed(() => site.value?.activation_status ?? null)
const siteAddress = computed(() => {
  const s = status.value?.stored?.site
  return [s?.address, [s?.zip, s?.city].filter(Boolean).join(' ')].filter(Boolean).join(', ')
})
const peakPower = computed(() =>
  formatPeakPower(live.value.site?.peak_power_kw ?? status.value?.stored?.site?.peak_power)
)

// live list when read, with the stored fields the API does not give (counter, last sample, firmware)
const inverters = computed(() => {
  const stored = status.value?.stored?.inverters ?? []
  const storedBySerial = Object.fromEntries(stored.map((i) => [i.serial_number, i]))
  const liveList = live.value.inverters
  const list = liveList?.length ? liveList : stored
  return list
    .map((i) => ({ ...(storedBySerial[i.serial_number] ?? {}), ...i }))
    .sort((a, b) => Number(b.active !== false) - Number(a.active !== false))
})
const activeCount = computed(() => inverters.value.filter((i) => i.active !== false).length)

const alerts = computed(() => {
  // live alerts lack status and error codes: take those from the stored ones
  const stored = status.value?.stored?.open_alerts ?? []
  if (!live.value.open_alerts) return stored
  const storedById = Object.fromEntries(stored.map((a) => [a.alert_id, a]))
  return live.value.open_alerts.map((a) => ({ ...(storedById[a.alert_id] ?? {}), ...a }))
})

// PANEL_COMMUNICATION_FAULT -> Panel communication fault
const alertLabel = (type) => {
  if (!type) return '—'
  const words = type.toLowerCase().split('_')
  return [words[0].charAt(0).toUpperCase() + words[0].slice(1), ...words.slice(1)].join(' ')
}

// red when older than a day: daytime samples and uploads come at least every few hours
const ageClass = (value) => {
  if (!value) return ''
  return Date.now() - new Date(value).getTime() > 24 * 3600 * 1000 ? 'tw-text-red-600' : ''
}
</script>
